
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import HomePage from '@/pages/HomePage';
import AboutPage from '@/pages/AboutPage';
import ContactPage from '@/pages/ContactPage';
import InsightsPage from '@/pages/InsightsPage';
import LoginPage from '@/pages/LoginPage';
import SignUpPage from '@/pages/SignUpPage';
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import UsersManagementPage from '@/pages/admin/UsersManagementPage';
import AdminPrivilegesPage from '@/pages/admin/AdminPrivilegesPage';
import EditUserPage from '@/pages/admin/EditUserPage';
import AdminVerificationPage from '@/pages/admin/AdminVerificationPage';
import CertificatesPage from '@/pages/admin/CertificatesPage';
import TemplatesListPage from '@/pages/admin/TemplatesListPage';
import TemplateFormPage from '@/pages/admin/TemplateFormPage';
import IssueCertificatePage from '@/pages/admin/IssueCertificatePage';
import CoursesManagementPage from '@/pages/admin/CoursesManagementPage';
import CoursesListPage from '@/pages/admin/courses/CoursesListPage';
import EditCoursePage from '@/pages/admin/courses/EditCoursePage';
import ModulesManagementPage from '@/pages/admin/courses/ModulesManagementPage';
import ModuleLessonsPage from '@/pages/admin/courses/ModuleLessonsPage';
import LessonContentPage from '@/pages/admin/courses/LessonContentPage';
import { Toaster } from "@/components/ui/toaster"
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthRoute } from './components/AuthRoute';
import PricingPage from './pages/PricingPage';
import CertificatesUserPage from './pages/CertificatesUserPage';
import CoursesUserPage from './pages/CoursesUserPage';
import CourseDetailsPage from './pages/CourseDetailsPage';
import LessonDetailsPage from './pages/LessonDetailsPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import CheckoutCancelPage from './pages/CheckoutCancelPage';
import UserProfilePage from './pages/UserProfilePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import InsightResourcesPage from './pages/admin/insights/InsightResourcesPage';
import Index from './pages/Index';
import ErrorBoundary from './components/ErrorBoundary';
import BlueprintPage from './pages/BlueprintPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import AssessmentPage from './pages/AssessmentPage';
import QuizPage from './pages/QuizPage';
import ResultsPage from './pages/ResultsPage';
import AuthPage from './pages/AuthPage';
import FAQPage from './pages/FAQPage';
import GlossaryPage from './pages/GlossaryPage';
import AdvisoryPage from './pages/AdvisoryPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ContinuousGovernancePage from './pages/ContinuousGovernancePage';
import BookPage from './pages/BookPage';

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Public Routes - Index is the main landing page, HomePage is now the dashboard */}
          <Route path="/" element={<Index />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/blueprint" element={<BlueprintPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/assessment" element={<AssessmentPage />} />
          <Route path="/quiz" element={<QuizPage />} />
          <Route path="/results" element={<ResultsPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/glossary" element={<GlossaryPage />} />
          <Route path="/advisory" element={<AdvisoryPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/continuous-governance" element={<ContinuousGovernancePage />} />
          <Route path="/book" element={<BookPage />} />
          
          {/* Auth Routes - Consolidate to have login route redirect to auth */}
          <Route path="/login" element={<Navigate to="/auth" replace />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/signup" element={<Navigate to="/blueprint" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* User Routes */}
          <Route path="/profile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
          <Route path="/certificates" element={<ProtectedRoute><CertificatesUserPage /></ProtectedRoute>} />
          <Route path="/courses" element={<ProtectedRoute><CoursesUserPage /></ProtectedRoute>} />
          <Route path="/courses/:courseId" element={<ProtectedRoute><CourseDetailsPage /></ProtectedRoute>} />
          <Route path="/courses/:courseId/lessons/:lessonId" element={<ProtectedRoute><LessonDetailsPage /></ProtectedRoute>} />
          <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />
          <Route path="/checkout/cancel" element={<ProtectedRoute><CheckoutCancelPage /></ProtectedRoute>} />
          
          {/* Admin Routes - All protected with adminOnly flag */}
          <Route path="/admin" element={<ProtectedRoute adminOnly={true}><AdminDashboardPage /></ProtectedRoute>} />
          <Route path="/admin/dashboard" element={<ProtectedRoute adminOnly={true}><AdminDashboardPage /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute adminOnly={true}><UsersManagementPage /></ProtectedRoute>} />
          <Route path="/admin/users/privileges" element={<ProtectedRoute adminOnly={true}><AdminPrivilegesPage /></ProtectedRoute>} />
          <Route path="/admin/users/edit/:userId" element={<ProtectedRoute adminOnly={true}><EditUserPage /></ProtectedRoute>} />
          <Route path="/admin/verification" element={<ProtectedRoute><AdminVerificationPage /></ProtectedRoute>} />
          
          {/* Admin Certificate Routes */}
          <Route path="/admin/certificates" element={<ProtectedRoute adminOnly={true}><CertificatesPage /></ProtectedRoute>} />
          <Route path="/admin/certificates/templates" element={<ProtectedRoute adminOnly={true}><TemplatesListPage /></ProtectedRoute>} />
          <Route path="/admin/certificates/templates/create" element={<ProtectedRoute adminOnly={true}><TemplateFormPage /></ProtectedRoute>} />
          <Route path="/admin/certificates/templates/edit/:id" element={<ProtectedRoute adminOnly={true}><TemplateFormPage /></ProtectedRoute>} />
          <Route path="/admin/certificates/issue" element={<ProtectedRoute adminOnly={true}><IssueCertificatePage /></ProtectedRoute>} />
          
          {/* Admin Course Management Routes - Updated to use correct path components */}
          <Route path="/admin/courses" element={<ProtectedRoute adminOnly={true}><CoursesManagementPage /></ProtectedRoute>} />
          <Route path="/admin/courses/list" element={<ProtectedRoute adminOnly={true}><CoursesListPage /></ProtectedRoute>} />
          <Route path="/admin/courses/edit/:courseId" element={<ProtectedRoute adminOnly={true}><EditCoursePage /></ProtectedRoute>} />
          <Route path="/admin/courses/modules/:courseId" element={<ProtectedRoute adminOnly={true}><ModulesManagementPage /></ProtectedRoute>} />
          <Route path="/admin/courses/modules/:courseId/:moduleId/lessons" element={<ProtectedRoute adminOnly={true}><ModuleLessonsPage /></ProtectedRoute>} />
          <Route path="/admin/courses/lessons/:lessonId/content" element={<ProtectedRoute adminOnly={true}><LessonContentPage /></ProtectedRoute>} />
          
          {/* Admin Insights Routes */}
          <Route path="/admin/insights/resources" element={<ProtectedRoute adminOnly={true}><InsightResourcesPage /></ProtectedRoute>} />
          
          {/* Default Route - Redirect to Home */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </ErrorBoundary>
  );
}

export default App;
